/**
 * Price changes (reconcile). The R13 HIGH is a design rule here: the read
 * THROWS on failure and renders a DISTINCT error state — a failed read must
 * never render as "all prices up to date". Keep = keep the operator's price
 * (acknowledged); Take file price = accept the file value.
 */
import { useEffect, useState } from 'preact/hooks';
import { Card } from '@ecmb/ui';
import { listRateConflicts, resolveRateConflict, type ApiRateConflict } from '../lib/actions.js';
import { serviceLabel, type Registry } from '../store/registry.js';
import { euro } from '../lib/format.js';
import { Banner } from '../components/Banner.js';

export type RateChangesProps = {
  registry: Registry;
  onResolved: () => void;
};

type LoadState =
  | { kind: 'loading' }
  | { kind: 'error' }
  | { kind: 'ready'; conflicts: ApiRateConflict[] };

export function RateChanges({ registry, onResolved }: RateChangesProps) {
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [failure, setFailure] = useState<string | null>(null);

  const load = () => {
    listRateConflicts()
      .then((conflicts) => setState({ kind: 'ready', conflicts }))
      .catch(() => setState({ kind: 'error' }));
  };
  useEffect(load, []);

  const resolve = async (rateId: number, action: 'keep' | 'accept') => {
    setFailure(null);
    if (await resolveRateConflict(rateId, action)) {
      load();
      onResolved();
    } else {
      setFailure("That didn't save. Try again in a moment.");
    }
  };

  return (
    <div class="screen-pane">
      <h1 class="screen-title" tabIndex={-1}>Price changes</h1>

      {failure && <Banner kind="error">{failure}</Banner>}

      {state.kind === 'loading' && <p class="helper-note">Checking for price changes…</p>}
      {/* DISTINCT error state — never "all up to date" on a failed read. */}
      {state.kind === 'error' && (
        <Banner kind="error">
          Failed to load price changes. There may be conflicts this screen cannot see right now.{' '}
          <button type="button" class="btn btn--quiet btn--inline" onClick={load}>Try again</button>
        </Banner>
      )}
      {state.kind === 'ready' && state.conflicts.length === 0 && (
        <p class="helper-note">All prices up to date. Changes in the price file land here for review.</p>
      )}

      {/* ⭐⭐ T1749 M10 (Step 2b) — THE PRICE-CHANGE CARD IS THE LIBRARY'S `Card`,
          IN THE `cover` SHAPE, AND THE LIST HOLDS THE SPACING. This screen drew
          its own box with `.inquiry-card`; four of that rule's five box
          declarations ARE `cover` exactly, the fifth is the shadow §3.3 reserves
          (the same ruled loss Inbox took at M8), and the two it declared that no
          shape carries — the 16px between cards and the 720px measure — are the
          LIST's, not the card's (§5.1). `.enquiry-list` carries both, on all
          three enquiry screens, which is what finally makes them agree. */}
      {state.kind === 'ready' && (
        <div class="enquiry-list">
          {state.conflicts.map((c) => (
            <Card key={c.rateId} shape="cover">
              <p class="enquiry-title">
                {serviceLabel(registry, c.service)} · {c.label} · {c.band}
              </p>
              {c.kind === 'conflict' ? (
                <>
                  <p class="enquiry-meta">
                    Your price: {c.storedPriceCents != null ? euro(c.storedPriceCents) : 'none'} · File price:{' '}
                    {c.filePriceCents != null ? euro(c.filePriceCents) : 'none'}
                  </p>
                  <div class="action-row action-row--card">
                    <button type="button" class="btn btn--secondary" onClick={() => void resolve(c.rateId, 'keep')}>
                      Keep mine
                    </button>
                    <button type="button" class="btn btn--quiet" onClick={() => void resolve(c.rateId, 'accept')}>
                      Take file price
                    </button>
                  </div>
                </>
              ) : (
                <p class="enquiry-meta">
                  Removed from the price file. Your price {c.storedPriceCents != null ? euro(c.storedPriceCents) : ''} still applies; review it in Price lists.
                </p>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
